import React from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, BookOpen, Shield, Calendar, Layers } from 'lucide-react';
import {
  generateMethodologyPdf,
  generateSecurityPdf,
  generateImplementationPdf,
  generateScopePdf,
} from '@/services/procurementPdfTemplates';

const documents = [
  {
    id: 'methodology',
    title: 'Methodology & Data Sources',
    description: 'Valuation framework, data inputs, scenario modeling approach, and source classification.',
    icon: BookOpen,
    pages: '1 page',
    generate: generateMethodologyPdf,
  },
  {
    id: 'security',
    title: 'Security & Privacy Overview',
    description: 'Tenant isolation, input privacy, anti-inference formatting, access control, and export policies.',
    icon: Shield,
    pages: '1 page',
    generate: generateSecurityPdf,
  },
  {
    id: 'implementation',
    title: 'Implementation Plan',
    description: 'Phased roadmap from pilot through data integration, scale, and production hardening.',
    icon: Calendar,
    pages: '1–2 pages',
    generate: generateImplementationPdf,
  },
  {
    id: 'scope',
    title: 'Pilot vs Production Scope',
    description: 'Side-by-side comparison of current pilot capabilities and planned production features.',
    icon: Layers,
    pages: '1 page',
    generate: generateScopePdf,
  },
];

// Tenant label map — must match backend TENANT_REGISTRY
const TENANT_LABELS = {
  'us-soccer': 'US SOCCER',
  'demo-tenant': 'DEMO',
};

function resolveTenantLabel() {
  const seg = window.location.pathname.split('/')[1] || '';
  return TENANT_LABELS[seg] || seg.toUpperCase().replace(/-/g, ' ') || 'YBV';
}

function openPdf(generateFn) {
  const win = window.open('', '_blank', 'width=850,height=1100');
  if (!win) {
    alert('Please allow pop-ups to generate PDF documents.');
    return;
  }
  let html = generateFn();
  // Tenant-scope watermarks
  const tenantLabel = resolveTenantLabel();
  html = html.replace(/US SOCCER PILOT/g, `${tenantLabel} PILOT`);
  win.document.write(html);
  win.document.close();
  win.onload = () => setTimeout(() => win.print(), 400);
}

function DocCard({ doc }) {
  const Icon = doc.icon;
  return (
    <Card className="bg-card border-border/50 hover:border-border transition-colors" data-testid={`procurement-doc-${doc.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground">{doc.title}</h3>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{doc.description}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30 border text-2xs">CONFIDENTIAL</Badge>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openPdf(doc.generate)}
            className="flex-shrink-0"
            data-testid={`download-${doc.id}`}
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Download
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProcurementKit() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30 border text-xs">CONFIDENTIAL</Badge>
            </div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="procurement-kit-title">
              US Soccer — Procurement Kit
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Downloadable documentation package for the U.S. Soccer Federation pilot engagement.
              All documents include confidentiality watermarks and generation timestamps.
            </p>
          </div>

          {/* Document Grid */}
          <div className="space-y-4" data-testid="procurement-doc-list">
            <DocCard doc={documents[0]} />
            <DocCard doc={documents[1]} />
            <DocCard doc={documents[2]} />
            <DocCard doc={documents[3]} />
          </div>

          {/* Footer note */}
          <div className="mt-8 p-4 rounded-lg border border-border/30 bg-muted/10">
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">
                  All documents reflect the current pilot-phase scope and use modeled baselines.
                  Production-phase documentation will be updated upon engagement approval.
                  For questions, contact your YBV platform administrator.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
